/** Column definitions for the notifications grid.
 *
 * All columns are `filterable: false` — filtering is driven entirely by the
 * QuerySearch widget. Sortable columns mirror the server's ordering whitelist
 * (name/event/enabled/tenant/created_ts/updated_ts). The `channels` column
 * renders one chip per delivery method (S = standard template, C = custom
 * source; dimmed when the method is disabled).
 */
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { Link as RouterLink } from 'react-router-dom';

import type { DeliveryMethodSummary, NotificationListItem } from '@/api/notifications';
import { BOOKING_STATE_LABELS, RENDER_STANDARD, TOUR_OVERRIDE_MODE_LABELS } from '@/api/notifications';
import { copyableColumn } from '@/components/DataGrid/copyableColumn';
import { WithTenantMark } from '@/components/TenantMark/TenantMark';
import { formatRelativeTime } from '@/utils/format';

import { deliveryLogsUrl } from './deliveryLogsLink';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

function channelTooltip(dm: DeliveryMethodSummary): string {
  const bits = [dm.method_label];
  if (dm.rendering_type != null) {
    bits.push(dm.rendering_type === RENDER_STANDARD ? 'Standard template' : 'Custom template');
  }
  if (dm.theme_codename) bits.push(`theme: ${dm.theme_codename}`);
  if (!dm.enabled) bits.push('disabled');
  return bits.join(' · ');
}

export function ChannelChips({ methods }: { methods: DeliveryMethodSummary[] }) {
  if (methods.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        no delivery methods
      </Typography>
    );
  }
  return (
    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
      {methods.map((dm) => (
        <Tooltip key={dm.id} title={channelTooltip(dm)}>
          <Chip
            size="small"
            variant="outlined"
            label={
              dm.rendering_type == null
                ? dm.method_label
                : `${dm.method_label} · ${dm.rendering_type === RENDER_STANDARD ? 'S' : 'C'}`
            }
            sx={{ height: 22, fontSize: 12, opacity: dm.enabled ? 1 : 0.45 }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}

/** Standard/Custom/Mixed across the composition-capable channels. */
export function renderingMix(methods: DeliveryMethodSummary[]): string {
  const kinds = new Set(
    methods
      .filter((dm) => dm.rendering_type != null)
      .map((dm) => (dm.rendering_type === RENDER_STANDARD ? 'standard' : 'custom')),
  );
  if (kinds.size === 0) return '—';
  if (kinds.size === 2) return 'Mixed';
  return kinds.has('standard') ? 'Standard' : 'Custom';
}

export function buildColumns({ showTenant }: { showTenant: boolean }): GridColDef<NotificationListItem>[] {
  const cols: GridColDef<NotificationListItem>[] = [
    copyableColumn({
      field: 'id',
      headerName: 'ID',
      width: 80,
      sortable: false,
      filterable: false,
    }),
  ];

  if (showTenant) {
    cols.push({
      field: 'tenant',
      headerName: 'Tenant',
      width: 150,
      sortable: true,
      filterable: false,
      valueGetter: (_v, row) => row.tenant?.code ?? '',
      renderCell: ({ row }) =>
        row.tenant ? (
          <WithTenantMark environment={row.tenant.environment} placement="under">
            <RouterLink to={`/tour-operators/${row.tenant.id}`}>{row.tenant.code}</RouterLink>
          </WithTenantMark>
        ) : (
          <Chip size="small" variant="outlined" label="System" sx={{ height: 22, fontSize: 12 }} />
        ),
    });
  }

  cols.push(
    {
      field: 'name',
      headerName: 'Name',
      flex: 1,
      minWidth: 220,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) => (
        <RouterLink to={`/notifications/${row.id}`} style={{ fontWeight: 500 }}>
          {row.name || `#${row.id}`}
        </RouterLink>
      ),
    },
    {
      field: 'event',
      headerName: 'Event',
      width: 230,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) => (
        <Typography component="span" sx={mono}>
          {row.event}
        </Typography>
      ),
    },
    {
      field: 'channels',
      headerName: 'Channels',
      flex: 1,
      minWidth: 220,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => <ChannelChips methods={row.delivery_methods} />,
    },
    {
      field: 'mix',
      headerName: 'Rendering',
      width: 110,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => renderingMix(row.delivery_methods),
    },
    {
      field: 'enabled',
      headerName: 'Enabled',
      width: 90,
      sortable: true,
      filterable: false,
      renderCell: ({ row }) =>
        row.enabled ? (
          <CheckCircleOutlineIcon color="success" fontSize="small" />
        ) : (
          <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
        ),
    },
    {
      field: 'tour_override_mode',
      headerName: 'Override',
      width: 190,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => TOUR_OVERRIDE_MODE_LABELS[row.tour_override_mode] ?? String(row.tour_override_mode),
    },
    {
      // booking_state is not in the server ordering whitelist — display-only.
      field: 'booking_state',
      headerName: 'Booking state',
      width: 130,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => BOOKING_STATE_LABELS[row.booking_state] ?? String(row.booking_state),
      renderCell: ({ row }) => (
        <Chip
          size="small"
          variant="outlined"
          color={row.booking_state === 1 ? 'success' : row.booking_state === 2 ? 'error' : 'default'}
          label={BOOKING_STATE_LABELS[row.booking_state] ?? String(row.booking_state)}
          sx={{ height: 22, fontSize: 12 }}
        />
      ),
    },
    {
      field: 'disable_default_blocks',
      headerName: 'No default blocks',
      width: 150,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) =>
        row.disable_default_blocks ? (
          <CheckCircleOutlineIcon color="success" fontSize="small" />
        ) : (
          <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
        ),
    },
    {
      field: 'language_policy',
      headerName: 'Language policy',
      width: 160,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => row.language_policy?.codename ?? '—',
    },
    {
      field: 'block_ref_count',
      headerName: 'Blocks',
      width: 80,
      sortable: false,
      filterable: false,
      valueGetter: (_v, row) => row.block_ref_count ?? 0,
    },
    {
      field: 'updated_ts',
      headerName: 'Updated',
      width: 140,
      sortable: true,
      filterable: false,
      valueGetter: (_v, row) => row.updated_ts,
      renderCell: ({ row }) => (
        <Tooltip title={row.updated_ts ?? ''}>
          <span>{formatRelativeTime(row.updated_ts)}</span>
        </Tooltip>
      ),
    },
    {
      field: 'delivery_logs',
      headerName: '',
      width: 56,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      align: 'center',
      renderCell: ({ row }) => (
        <Tooltip title="Delivery logs for this notification (last 7 days) — opens in a new tab.">
          <IconButton
            size="small"
            aria-label={`Delivery logs for notification #${row.id}`}
            component="a"
            href={deliveryLogsUrl(row.id)}
            target="_blank"
            rel="noopener"
            onClick={(e) => e.stopPropagation()}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  );

  return cols;
}
